/**
 * RR7 root module — app/root.tsx
 *
 * Sets up:
 * - CAPE B2B data-theme on <html>, resolved per-request from brand/mode cookies or query params
 * - i18next / react-i18next provider
 * - QueryClientProvider (TanStack Query)
 * - Root ErrorBoundary
 */
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { I18nextProvider } from "react-i18next";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteLoaderData,
} from "react-router";
import "@deliveryhero/cape-tokens/themes/core/core.css";

import type { Route } from "./+types/root";
import { brandDataTheme, resolveTheme } from "~/lib/brand";
import { getThemeUrl } from "~/lib/theme-urls";
import { i18n } from "~/lib/i18n";
import "@deliveryhero/cape-core/dist/assets/css-normalize.css";
import * as styles from "./root.css";

export function loader({ request }: Route.LoaderArgs) {
  const { brand, mode } = resolveTheme(request);
  return { brand, mode };
}

export const links: Route.LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Figtree:wght@300;400;500;600;700&display=swap",
  },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const loaderData = useRouteLoaderData<typeof loader>("root");
  const brand = loaderData?.brand ?? "foodpanda";
  const mode = loaderData?.mode ?? "light";
  const dataTheme = brandDataTheme(brand, mode);
  const themeUrl = getThemeUrl(brand, mode);

  return (
    <html lang="en" data-theme={dataTheme} dir="ltr">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {themeUrl != null && <link rel="stylesheet" href={themeUrl} />}
        <Meta />
        <Links />
      </head>
      <body style={{ fontFamily: "Figtree, var(--cp-font-family-default, sans-serif)" }}>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  // QueryClient is stable per session — re-created only on remount
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Aggressive staleTime — loaders are the primary data source.
            // TanStack Query is only used for F03/F10 polling.
            staleTime: 30_000,
            retry: 1,
          },
        },
      }),
  );

  // Restore language preference from localStorage and keep <html> attrs in sync.
  // This runs client-side only — SSR always renders lang="en" dir="ltr".
  useEffect(() => {
    function updateHtmlAttrs(lang: string) {
      document.documentElement.lang = lang;
      document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    }

    i18n.on("languageChanged", updateHtmlAttrs);

    const saved = localStorage.getItem("portal_lang");
    if (saved && saved !== "en") {
      void i18n.changeLanguage(saved);
    }

    return () => {
      i18n.off("languageChanged", updateHtmlAttrs);
    };
  }, []);

  return (
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <Outlet />
      </QueryClientProvider>
    </I18nextProvider>
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  // useTranslation() cannot be used here — ErrorBoundary renders outside the
  // I18nextProvider tree defined in App(). The singleton is used directly as
  // a workaround; if language switching is added, this boundary will always
  // render in the language active at the time of the error.
  const t = i18n.t.bind(i18n);

  let heading = t("error.default.heading");
  let message = t("error.default.message");

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      heading = t("error.404.heading");
      message = t("error.404.message");
    } else if (error.status === 403) {
      heading = t("error.403.heading");
      message = t("error.403.message");
    } else if (error.status === 401) {
      heading = t("error.401.heading");
      message = t("error.401.message");
    }
  }

  return (
    <div role="alert" className={styles.errorContainer}>
      <div className={styles.errorCard}>
        <h1 className={styles.errorHeading}>{heading}</h1>
        <p className={styles.errorMessage}>{message}</p>
      </div>
    </div>
  );
}
